// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { useLocationDetail } from '@ta/common.routing';
import { TrackingNamespaceContext } from '@ta/tracking.interactions';
import { sessionType as getSessionType } from '@ta/social.feed';
import useObject from './useObject';

export type ContextInfo = {|
  namespace?: string,
  placement?: string,
  locationId?: number,
  objectTypes?: string[],
  impressionId?: string,
  sectionId?: string,
  itemId?: string,
  sessionType?: string,
  owner?: string | null,
  actor?: string,
  puid?: string,
|};

export type ContextStack = {|
  previousContext: ContextStack | null,
  ...ContextInfo,
|};

type Props = {|
  ...ContextInfo,
  children: React.Node,
|};

export const MentionsTrackingContext = React.createContext<ContextStack>({ previousContext: null });

type OptionalNamespaceProviderProps = {|
  namespace?: string,
  children: React.Node,
|};

// Interaction logging needs namespace context
const OptionalNamespaceProvider = ({ namespace, children }: OptionalNamespaceProviderProps) =>
  namespace ? (
    <TrackingNamespaceContext.Provider value={namespace}>
      {children}
    </TrackingNamespaceContext.Provider>
  ) : (
    children
  );

/**
 * Returns a ContextInfo object with entry values matching undefined filtered out
 * @param info the info object to filter
 * @returns {ContextInfo}
 */
const filterUndefined = (info: ContextInfo): ContextInfo => {
  const newInfo = {};
  Object.entries(info).forEach(([key, value]) => {
    if (value !== undefined) {
      newInfo[key] = value;
    }
  });
  return (newInfo: any);
};

/**
 *
 * A context which forms a stack trace up the tree. This solves the problem of having too many contexts when we don't care about the value changing
 */
const UnconnectedMentionsTrackingProvider = ({ children, ...trackingArgs }: Props) => {
  const previousContext = React.useContext(MentionsTrackingContext);
  // An entry will still be assigned even if it's undefined, so need to ensure if its undefined that it's never assigned at all
  const value = useObject<ContextStack>({
    previousContext,
    ...filterUndefined(trackingArgs),
  });
  return (
    <OptionalNamespaceProvider namespace={trackingArgs.namespace}>
      <MentionsTrackingContext.Provider value={value}>{children}</MentionsTrackingContext.Provider>
    </OptionalNamespaceProvider>
  );
};

const MentionsTrackingProviderWithLocationId = (props: Props) => {
  const locationId = useLocationDetail();
  return <UnconnectedMentionsTrackingProvider locationId={locationId || undefined} {...props} />;
};

const ConnectedMentionsTrackingProvider = connect<Props, Props, _, _, _, _>((state, ownProps) => ({
  sessionType: getSessionType(state.meta.device.viewportCategory),
  owner: state.auth.loggedInUserId,
  puid: state.tracking.uid,
  ...ownProps, // Have own props take precedence if needed
}))(MentionsTrackingProviderWithLocationId);

const MentionsTrackingProvider = (props: Props) => {
  const { previousContext } = React.useContext(MentionsTrackingContext);
  // This ensures only the top-most level Provider is subscribed to redux
  if (!previousContext) {
    return <ConnectedMentionsTrackingProvider {...props} />;
  }
  return <UnconnectedMentionsTrackingProvider {...props} />;
};

export { MentionsTrackingProvider };

const trackingStackToArray = (stack: ContextStack): ContextStack[] => {
  if (stack.previousContext) {
    const array = trackingStackToArray(stack.previousContext);
    array.push(stack);
    return array;
  }
  return [stack];
};

export const aggregateTrackingStack = (stack: ContextStack): ContextInfo => {
  const trackingStackArray = trackingStackToArray(stack);
  const info = {};
  trackingStackArray.forEach(trace => {
    Object.assign(info, trace);
  });
  return ((info: any): ContextInfo);
};
